/**
 * Create a contact.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { createContactInput, type Organization } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { ContactFields, readContactForm } from '../components/contact-form'
import { Button, PageHeader } from '../ui'

interface Data {
  organizations: Organization[]
  presetOrganizationId: string | null
}

/**
 * `?organization=<id>` pre-fills the picker, so "add a contact" from an
 * organization screen lands here with the link already made.
 */
export async function loader({ request }: LoaderFunctionArgs): Promise<Data> {
  const url = new URL(request.url)
  const page = await api.organizations.list({ limit: 200, sort: 'name', direction: 'asc' })
  return {
    organizations: page.rows,
    presetOrganizationId: url.searchParams.get('organization'),
  }
}

export async function action({ request }: ActionFunctionArgs): Promise<FormResult | Response> {
  const parsed = createContactInput.safeParse(readContactForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    const contact = await api.contacts.create(parsed.data)
    return redirect(`/crm/contacts/${contact.id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function NewContact() {
  const { organizations, presetOrganizationId } = useLoaderData() as Data
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title="New contact" />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <ContactFields
          organizations={organizations}
          presetOrganizationId={presetOrganizationId}
          issues={result?.issues ?? {}}
        />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create contact'}
          </Button>
          <a className="ax-btn" href="/crm/contacts">
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
